import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { clearAllTrustedDeviceTokens } from '@/services/trustedDevice';
import { authApi } from '@/services/api';
import {
  UserCircleIcon,
  KeyIcon,
  BellIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar';

dayjs.extend(relativeTime);
dayjs.locale('ar');

const profileSchema = z.object({
  fullName: z.string().min(1, 'الاسم مطلوب'),
  email: z.string().email('البريد الإلكتروني غير صالح'),
  phone: z.string().optional(),
});

const passwordSchema = z.object({
  oldPassword: z.string().min(6, 'كلمة المرور الحالية مطلوبة'),
  newPassword: z.string().min(8, 'كلمة المرور الجديدة يجب أن تكون 8 أحرف على الأقل'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'كلمات المرور غير متطابقة',
  path: ['confirmPassword'],
});

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

const tabs = [
  { id: 'profile', name: 'الملف الشخصي', icon: UserCircleIcon },
  { id: 'security', name: 'الأمان', icon: KeyIcon },
  { id: 'notifications', name: 'الإشعارات', icon: BellIcon },
  { id: 'system', name: 'النظام', icon: Cog6ToothIcon },
];

export default function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  const { user, updateUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      fullName: user?.fullName || '',
      email: user?.email || '',
      phone: user?.phone || '',
    },
  });

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  });

  const onProfileSubmit = async (data: ProfileForm) => {
    setIsLoading(true);
    try {
      // API call would go here
      updateUser(data);
      toast.success('تم تحديث الملف الشخصي بنجاح');
    } catch (error) {
      toast.error('فشل في تحديث الملف الشخصي');
    } finally {
      setIsLoading(false);
    }
  };

  const onPasswordSubmit = async (data: PasswordForm) => {
    setIsLoading(true);
    try {
      await authApi.changePassword(data.oldPassword, data.newPassword);
      toast.success('تم تغيير كلمة المرور بنجاح');
      passwordForm.reset();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل في تغيير كلمة المرور');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">الإعدادات</h1>
        <p className="text-gray-500 mt-1">إدارة حسابك وإعدادات النظام</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="w-full lg:w-64 flex-shrink-0">
          <div className="card">
            <nav className="p-2 space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-right transition-colors',
                    activeTab === tab.id
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  )}
                >
                  <tab.icon className="w-5 h-5" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">الملف الشخصي</h3>
                <p className="text-sm text-gray-500">تحديث معلوماتك الشخصية</p>
              </div>
              <div className="card-body">
                <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-4">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center">
                      <UserCircleIcon className="w-12 h-12 text-primary-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{user?.fullName}</h4>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {user?.role === 'super_admin' ? 'مدير النظام' :
                         user?.role === 'ministry_admin' ? 'مدير الوزارة' :
                         user?.role === 'hospital_admin' ? 'مدير المستشفى' :
                         user?.role === 'dispatcher' ? 'مشرف' : 'مستخدم'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="label">الاسم الكامل</label>
                    <input
                      {...profileForm.register('fullName')}
                      className={clsx('input', profileForm.formState.errors.fullName && 'input-error')}
                    />
                    {profileForm.formState.errors.fullName && (
                      <p className="error-message">{profileForm.formState.errors.fullName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">البريد الإلكتروني</label>
                    <input
                      {...profileForm.register('email')}
                      className={clsx('input', profileForm.formState.errors.email && 'input-error')}
                      dir="ltr"
                    />
                    {profileForm.formState.errors.email && (
                      <p className="error-message">{profileForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">رقم الهاتف</label>
                    <input
                      {...profileForm.register('phone')}
                      className="input"
                      dir="ltr"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? <div className="spinner w-5 h-5" /> : 'حفظ التغييرات'}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">الأمان</h3>
                <p className="text-sm text-gray-500">تغيير كلمة المرور وإعدادات الأمان</p>
              </div>
              <div className="card-body">
                <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4 max-w-md">
                  <div>
                    <label className="label">كلمة المرور الحالية</label>
                    <input
                      type="password"
                      {...passwordForm.register('oldPassword')}
                      className={clsx('input', passwordForm.formState.errors.oldPassword && 'input-error')}
                    />
                    {passwordForm.formState.errors.oldPassword && (
                      <p className="error-message">{passwordForm.formState.errors.oldPassword.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">كلمة المرور الجديدة</label>
                    <input
                      type="password"
                      {...passwordForm.register('newPassword')}
                      className={clsx('input', passwordForm.formState.errors.newPassword && 'input-error')}
                    />
                    {passwordForm.formState.errors.newPassword && (
                      <p className="error-message">{passwordForm.formState.errors.newPassword.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">تأكيد كلمة المرور الجديدة</label>
                    <input
                      type="password"
                      {...passwordForm.register('confirmPassword')}
                      className={clsx('input', passwordForm.formState.errors.confirmPassword && 'input-error')}
                    />
                    {passwordForm.formState.errors.confirmPassword && (
                      <p className="error-message">{passwordForm.formState.errors.confirmPassword.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? <div className="spinner w-5 h-5" /> : 'تغيير كلمة المرور'}
                  </button>
                </form>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <ActiveSessionsPanel />
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">الإشعارات</h3>
                <p className="text-sm text-gray-500">إدارة تفضيلات الإشعارات</p>
              </div>
              <div className="card-body">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">إشعارات التنبيهات</p>
                      <p className="text-sm text-gray-500">استلام إشعارات عند حدوث تنبيهات جديدة</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">إشعارات الصيانة</p>
                      <p className="text-sm text-gray-500">تذكير بمواعيد صيانة المركبات</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">إشعارات البريد الإلكتروني</p>
                      <p className="text-sm text-gray-500">استلام ملخص يومي عبر البريد</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">إشعارات الطوارئ</p>
                      <p className="text-sm text-gray-500">إشعارات فورية لحالات الطوارئ</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* System Tab */}
          {activeTab === 'system' && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">إعدادات النظام</h3>
                <p className="text-sm text-gray-500">إعدادات عامة للنظام</p>
              </div>
              <div className="card-body">
                <div className="space-y-6">
                  <SystemSettingsForm />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Lists every active access-token JWT issued to the current user so they
 * can see "where am I logged in" and revoke individual ones (e.g. lost
 * laptop). The current device is marked + can't be revoked from itself
 * — use "إنهاء بقية الجلسات" for that.
 */
function ActiveSessionsPanel() {
  const [sessions, setSessions] = useState<Array<{
    jti: string;
    userAgent: string | null;
    ipAddress: string | null;
    createdAt: string;
    lastSeenAt: string;
    expiresAt: string;
    isCurrent: boolean;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const resp = await authApi.listSessions();
      setSessions(resp.data?.data || []);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'تعذّر تحميل الجلسات');
    } finally {
      setLoading(false);
    }
  };

  // Load once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useState(() => { load(); });

  const revokeOne = async (jti: string) => {
    setBusy(jti);
    try {
      await authApi.revokeSession(jti);
      toast.success('تم إنهاء الجلسة');
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'فشل إنهاء الجلسة');
    } finally {
      setBusy(null);
    }
  };

  const revokeOthers = async () => {
    if (!window.confirm('سيتم إنهاء جميع الجلسات الأخرى (تبقى الجلسة الحالية فقط). هل تريد المتابعة؟')) return;
    setBusy('others');
    try {
      const resp = await authApi.revokeOtherSessions();
      toast.success(resp.data?.message || 'تم الإنهاء');
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'فشل إنهاء الجلسات');
    } finally {
      setBusy(null);
    }
  };

  /**
   * Forget every device previously marked trusted, so each one must pass OTP
   * again. Clears the local copy too — leaving it behind would mean sending a
   * token the server has already revoked on every subsequent login.
   */
  const revokeTrusted = async () => {
    if (!window.confirm(
      'سيُطلب رمز التحقق من جميع الأجهزة الموثوقة عند الدخول القادم. هل تريد المتابعة؟',
    )) return;
    setBusy('trusted');
    try {
      const resp = await authApi.revokeTrustedDevices();
      clearAllTrustedDeviceTokens();
      toast.success(resp.data?.message || 'تم إلغاء الأجهزة الموثوقة');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'فشل إلغاء الأجهزة الموثوقة');
    } finally {
      setBusy(null);
    }
  };

  const formatUA = (ua: string | null) => {
    if (!ua) return 'جهاز غير معروف';
    // Coarse but readable: detect browser + OS family without pulling
    // a full UA parser dependency.
    const browser =
      /OPR\//.test(ua) ? 'Opera' :
      /Edg\//.test(ua) ? 'Edge' :
      /Chrome\//.test(ua) ? 'Chrome' :
      /Safari\//.test(ua) ? 'Safari' :
      /Firefox\//.test(ua) ? 'Firefox' : 'متصفح';
    const os =
      /Windows/.test(ua) ? 'Windows' :
      /Android/.test(ua) ? 'Android' :
      /iPhone|iPad/.test(ua) ? 'iOS' :
      /Mac OS/.test(ua) ? 'macOS' :
      /Linux/.test(ua) ? 'Linux' : '';
    return os ? `${browser} على ${os}` : browser;
  };

  const otherCount = sessions.filter((s) => !s.isCurrent).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-gray-900">جلسات تسجيل الدخول النشطة</h4>
        {otherCount > 0 && (
          <button
            onClick={revokeOthers}
            disabled={busy === 'others'}
            className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
          >
            إنهاء بقية الجلسات ({otherCount})
          </button>
        )}
      </div>

      {/* Device trust is separate from sessions: signing out does not undo it,
          which is the whole point — so it needs its own way to be revoked. */}
      <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5
                      flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-800">الأجهزة الموثوقة</p>
          <p className="text-xs text-gray-500">
            الأجهزة التي اخترت اعتبارها موثوقة تتخطى رمز التحقق عند الدخول
          </p>
        </div>
        <button
          onClick={revokeTrusted}
          disabled={busy === 'trusted'}
          className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50 shrink-0"
        >
          إلغاء ثقة جميع الأجهزة
        </button>
      </div>

      {loading ? (
        <div className="text-center text-gray-500 py-6">جارٍ التحميل…</div>
      ) : sessions.length === 0 ? (
        <div className="text-center text-gray-500 py-6 text-sm">
          لا توجد جلسات مسجّلة (سيتم التسجيل بدءاً من الجلسات القادمة).
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map((s) => (
            <div
              key={s.jti}
              className={clsx(
                'flex items-start sm:items-center justify-between gap-3 p-3 sm:p-4 rounded-lg',
                s.isCurrent ? 'bg-green-50 border border-green-200' : 'bg-gray-50',
              )}
            >
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <ShieldCheckIcon className={clsx(
                  'w-5 h-5 flex-shrink-0 mt-0.5',
                  s.isCurrent ? 'text-green-600' : 'text-gray-400',
                )} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
                    {formatUA(s.userAgent)}
                    {s.isCurrent && <span className="badge badge-success ml-2 text-[10px]">الحالية</span>}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate" dir="ltr">
                    {s.ipAddress || 'IP غير متوفر'}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {dayjs(s.createdAt).format('YYYY/MM/DD HH:mm')} ·{' '}
                    تنتهي {dayjs(s.expiresAt).fromNow()}
                  </p>
                </div>
              </div>
              {!s.isCurrent && (
                <button
                  onClick={() => revokeOne(s.jti)}
                  disabled={busy === s.jti}
                  className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 flex-shrink-0"
                >
                  إنهاء
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * System preferences form. Reads from + writes to settingsStore so
 * changes propagate to the rest of the app and persist across reloads
 * via localStorage (zustand/persist).
 */
function SystemSettingsForm() {
  const { language, timezone, distanceUnit, speedUnit,
          setLanguage, setTimezone, setDistanceUnit, setSpeedUnit } = useSettingsStore();

  const onChange = <T,>(setter: (v: T) => void, value: T, label: string) => {
    setter(value);
    toast.success(`تم تحديث ${label}`);
  };

  return (
    <>
      <div>
        <label className="label">اللغة</label>
        <select
          className="input w-full max-w-xs"
          value={language}
          onChange={(e) => onChange(setLanguage, e.target.value as 'ar' | 'en', 'اللغة')}
        >
          <option value="ar">العربية</option>
          <option value="en">English</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          تغيير اللغة يقلب اتجاه الواجهة بين RTL و LTR.
        </p>
      </div>

      <div>
        <label className="label">المنطقة الزمنية</label>
        <select
          className="input w-full max-w-xs"
          value={timezone}
          onChange={(e) => onChange(setTimezone, e.target.value, 'المنطقة الزمنية')}
        >
          <option value="Africa/Tripoli">توقيت طرابلس (GMT+2)</option>
          <option value="UTC">UTC</option>
          <option value="Europe/Istanbul">إسطنبول (GMT+3)</option>
          <option value="Asia/Riyadh">الرياض (GMT+3)</option>
          <option value="Asia/Dubai">دبي (GMT+4)</option>
          <option value="Europe/London">لندن (GMT+0/+1)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          تُطبَّق على عرض الأوقات في كل الصفحات (آخر اتصال، التاريخ،
          التنبيهات، السجلات).
        </p>
      </div>

      <div>
        <label className="label">وحدة المسافة</label>
        <select
          className="input w-full max-w-xs"
          value={distanceUnit}
          onChange={(e) => onChange(setDistanceUnit, e.target.value as 'km' | 'mi', 'وحدة المسافة')}
        >
          <option value="km">كيلومتر</option>
          <option value="mi">ميل</option>
        </select>
      </div>

      <div>
        <label className="label">وحدة السرعة</label>
        <select
          className="input w-full max-w-xs"
          value={speedUnit}
          onChange={(e) => onChange(setSpeedUnit, e.target.value as 'kmh' | 'mph', 'وحدة السرعة')}
        >
          <option value="kmh">كم/ساعة</option>
          <option value="mph">ميل/ساعة</option>
        </select>
      </div>
    </>
  );
}
